import { Component, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { PayloadGroupsService } from '@/services/api/payload-groups.service';
import { ContentGroupsService } from '@/services/api/content-groups.service';
import { PayloadGroup } from '@/models/content-group.model';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';

export interface ContentGroup {
  payloadId: number | null;
  name: string;
  cGroupId: number;
}

type severity = "success" | "secondary" | "info" | "warn" | "danger" | "contrast" | undefined;

interface severityObject {
  label: string;
  severity: severity;
}


@Component({
  selector: 'app-content-group-linker',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    TableModule, 
    SelectModule, 
    ButtonModule, 
    TagModule, 
    DialogModule,
    InputTextModule,
],
  template: `
  <div class="p-4 space-y-6">

    <!-- PAYLOAD GROUPS TABLE -->
    <h2 class="text-xl font-semibold">Payload Groups (Source)</h2>
    <p-table [value]="payloadGroups">
      <ng-template pTemplate="header">
        <tr>
          <th>Title</th>
          <th>Slug</th>
          <th>Status</th>
          <th>Link To Content Group</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-row>
        <tr>
          <td>{{ row.title }}</td>
          <td>{{ row.slug }}</td>
          <td>
            <p-tag [severity]="getPayloadStatus(row.id).severity"
              value="{{ getPayloadStatus(row.id).label }}">
            </p-tag>
          </td>
          <td>
          <div class="flex gap-2 items-center">
  <p-select
    appendTo="body"
    [options]="contentGroups()"
    optionLabel="name"
    optionValue="cGroupId"
    [ngModel]="getLinkedCGroupId(row.id)"
    placeholder="Assign"
    (onChange)="linkPayloadToContent(row.id, $event.value)">
  </p-select>

  <button
    pButton
    icon="pi pi-plus"
    class="p-button-rounded p-button-sm"
    (click)="openCreateDialog(row.id)">
  </button>
</div>
          </td>
        </tr>
      </ng-template>
    </p-table>


    <!-- ORPHANED CONTENT GROUPS -->
    <h2 class="text-xl font-semibold mt-10">Orphaned Content Groups</h2>
    <p-table [value]="orphanedContentGroups()">
      <ng-template pTemplate="header">
        <tr>
          <th>Name</th>
          <th>Payload ID</th>
          <th>Status</th>
          <th>Fix Link</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-row>
        <tr>
          <td>{{ row.name }}</td>
          <td>{{ row.payloadId }}</td>
          <td>
            <p-tag value="Invalid Payload ID" severity="danger"></p-tag>
          </td>
          <td>
            <p-select 
            appendTo="body"
              [options]="payloadGroups" 
              optionLabel="title"
              optionValue="id"
              placeholder="Reassign"
              (onChange)="fixInvalidLink(row.cGroupId, $event.value)">
            </p-select>
          </td>
        </tr>
      </ng-template>
    </p-table>

  </div>

  <p-dialog
  header="Create Content Group"
  [(visible)]="createDialogVisible"
  [modal]="true"
  [closable]="true"
  [style]="{ width: '350px' }">

  <div class="flex flex-col gap-4">
    <input
      type="text"
      pInputText
      placeholder="Group name"
      [(ngModel)]="newGroupName"
    />
  </div>

  <ng-template pTemplate="footer">
    <button
      pButton
      label="Cancel"
      class="p-button-text"
      (click)="createDialogVisible = false">
    </button>

    <button
      pButton
      label="Create"
      (click)="submitCreate()"
      [disabled]="!newGroupName">
    </button>
  </ng-template>
</p-dialog>

  `,
})
export class ContentGroupLinkerComponent {
  payloadGroupsService = inject(PayloadGroupsService);
  contentGroupsService = inject(ContentGroupsService);

contentGroups = computed(() => this.contentGroupsService.groups());

  get payloadGroups() {
    return this.payloadGroupsService.groups();
  }

  set payloadGroups(groups: PayloadGroup[]) {
    this.payloadGroupsService.groups.set(groups);
  }

  orphanedContentGroups = computed(() => {
    const payloadIds = new Set(this.payloadGroups.map(p => p.id));
    return this.contentGroups().filter(c => !payloadIds.has(c.payloadId || -1));
  });

  getLinkedCGroupId(payloadId: number): number | null {
    const match = this.contentGroups().find(c => c.payloadId === payloadId);
    return match ? match.cGroupId : null;
  }

  getPayloadStatus(payloadId: number):severityObject  {
    const cGroup = this.contentGroups().find(c => c.payloadId === payloadId);
    if (!cGroup) {
      return { label: 'Unassigned', severity: 'warn' } as severityObject;
    }
    return { label: `Linked to ${cGroup.name}`, severity: 'success' } as severityObject;
  }

  createDialogVisible = false;
newGroupName = '';
payloadToLinkOnCreate: number | null = null;


  // -------------------------------
  // Actions
  // -------------------------------
  linkPayloadToContent(payloadId: number, cGroupId: number) {
    this.contentGroupsService.updateGroup(cGroupId, payloadId);
  }

  fixInvalidLink(cGroupId: number, newPayloadId: number) {
    this.contentGroupsService.updateGroup(cGroupId, newPayloadId);
  }

  openCreateDialog(payloadId: number) {
    this.payloadToLinkOnCreate = payloadId;
    this.newGroupName = '';
    this.createDialogVisible = true;
  }
  
  submitCreate() {
    const name = this.newGroupName.trim();
    if (!name) return;
  
    this.contentGroupsService.createGroup(name, this.payloadToLinkOnCreate);
  
    this.createDialogVisible = false;
    this.newGroupName = '';
    this.payloadToLinkOnCreate = null;
  }
  
  
}
